export type Oklch = [lightness: number, chroma: number, hueRadians: number]

export function normalizeHex(seed: string): string {
	if (typeof seed !== 'string' || !/^#?(?:[a-f\d]{3}|[a-f\d]{6})$/i.test(seed)) {
		throw new TypeError('Expected an opaque three- or six-digit sRGB hex color')
	}
	const digits = seed.replace('#', '').toLowerCase()
	return '#' + (digits.length === 3 ? [...digits].map(value => value + value).join('') : digits)
}

export function rgbFromHex(hex: string): [number, number, number] {
	const normalized = normalizeHex(hex)
	return [
		parseInt(normalized.slice(1, 3), 16),
		parseInt(normalized.slice(3, 5), 16),
		parseInt(normalized.slice(5, 7), 16),
	]
}

function linear(channel: number): number {
	const value = channel / 255
	return value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4
}

export function linearRgbFromHex(hex: string): [number, number, number] {
	const [r, g, b] = rgbFromHex(hex)
	return [linear(r), linear(g), linear(b)]
}

// OKLab equations: Björn Ottosson, https://bottosson.github.io/posts/oklab/.
export function oklabFromLinear([r, g, b]: readonly [number, number, number]): [number, number, number] {
	const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b)
	const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b)
	const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b)
	return [
		0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s,
		1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s,
		0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s,
	]
}

export function oklchFromHex(hex: string): Oklch {
	const [L, a, b] = oklabFromLinear(linearRgbFromHex(hex))
	const C = Math.hypot(a, b)
	return [L, C < 1e-7 ? 0 : C, C < 1e-7 ? 0 : Math.atan2(b, a)]
}

function linearFromOklch(L: number, C: number, hueRadians: number): [number, number, number] {
	const a = C * Math.cos(hueRadians)
	const b = C * Math.sin(hueRadians)
	const l = (L + 0.3963377774 * a + 0.2158037573 * b) ** 3
	const m = (L - 0.1055613458 * a - 0.0638541728 * b) ** 3
	const s = (L - 0.0894841775 * a - 1.291485548 * b) ** 3
	return [
		4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
		-1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
		-0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
	]
}

export function hexFromOklch(L: number, C: number, hueRadians: number): string {
	if (![L, C, hueRadians].every(Number.isFinite) || L < 0 || L > 1 || C < 0) {
		throw new RangeError('Invalid OKLCH color')
	}
	if (L === 0) return '#000000'
	if (L === 1) return '#ffffff'
	// sRGB never needs chroma above one; bound the search even for huge requests.
	C = Math.min(C, 1)
	let channels = linearFromOklch(L, C, hueRadians)
	// The inverse matrices put saturated yellow outside RGB by 1.31e-7.
	if (!channels.every(channel => channel >= -2e-7 && channel <= 1 + 2e-7)) {
		let low = 0
		let high = C
		for (let iteration = 0; iteration < 12; iteration++) {
			const middle = (low + high) / 2
			if (linearFromOklch(L, middle, hueRadians).every(channel => channel >= -2e-7 && channel <= 1 + 2e-7)) {
				low = middle
			} else high = middle
		}
		channels = linearFromOklch(L, low, hueRadians)
	}
	return (
		'#' +
		channels
			.map(channel => {
				const value = Math.max(0, Math.min(1, channel))
				return Math.round(255 * (value <= 0.0031308 ? 12.92 * value : 1.055 * value ** (1 / 2.4) - 0.055))
					.toString(16)
					.padStart(2, '0')
			})
			.join('')
	)
}

export function relativeLuminance(hex: string): number {
	const [r, g, b] = linearRgbFromHex(hex)
	return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

function contrastFromLuminance(first: number, second: number): number {
	return (Math.max(first, second) + 0.05) / (Math.min(first, second) + 0.05)
}

export function contrastRatio(first: string, second: string): number {
	return contrastFromLuminance(relativeLuminance(first), relativeLuminance(second))
}

export function contrastInk(
	L: number,
	C: number,
	hueRadians: number,
	backgrounds: readonly string[],
	floor: number,
	darker: boolean,
): string {
	if (!backgrounds.length || !Number.isFinite(floor) || floor < 1 || floor > 21) {
		throw new RangeError('Invalid contrast target')
	}
	const grounds = backgrounds.map(relativeLuminance)
	let failing = L
	let passing = darker ? 0 : 1
	let result = hexFromOklch(L, C, hueRadians)
	let resultY = relativeLuminance(result)
	if (grounds.every(ground => contrastFromLuminance(resultY, ground) >= floor)) return result
	result = hexFromOklch(passing, C, hueRadians)
	resultY = relativeLuminance(result)
	if (!grounds.every(ground => contrastFromLuminance(resultY, ground) >= floor)) {
		throw new RangeError('Unreachable contrast target')
	}
	for (let iteration = 0; iteration < 12; iteration++) {
		const middle = (passing + failing) / 2
		const candidate = hexFromOklch(middle, C, hueRadians)
		const candidateY = relativeLuminance(candidate)
		if (grounds.every(ground => contrastFromLuminance(candidateY, ground) >= floor)) {
			passing = middle
			result = candidate
		} else failing = middle
	}
	return result
}
